// src/components/PurchaseLimitsAdmin.js
// Payment Settings "Purchase limits" (admin only; STEP3_5A_TIERS_DESIGN.md
// 3.6, 3.7 and 5.14; release R3).
//
// Three blocks, each on its own callable (tipReview.js):
//  - the two switches on the server-only tipReview/config doc: the mode
//    (Daily cap / Purchase limits) and Card checks, plus the private review
//    line and hold days. Saving needs a note (audit row) and calls
//    adminSetTipLimitConfig; warm instances pick it up within 45 s. The
//    current config arrives with adminListTipReviewShares (its only read).
//  - "Shares in review": creators' shares on a longer review hold, and
//    withheld ones (adminListTipReviewShares / adminReviewTipShare).
//  - "Cards": blocked and busy cards (adminListCardLimits / adminSetCardBlock).
//    Each row's "Payments" opens that card's detail (adminListCardLimits
//    {cardKey}): its Super Crits and Rumbles with "Withhold" (adminReviewTipShare
//    by paymentIntentId) and its membership charges with "Withhold" / "Release"
//    on the credit (adminWithholdMembershipCredit). That is the path the
//    card_block alert names ("Cards, then Shares in review or the membership
//    credit"); a withheld tip share then shows under Shares in review.
//
// The 3D Secure scope (threeDS) is never sent from here: the R6 Disputes
// block owns it, and adminSetTipLimitConfig keeps the stored value when it
// is omitted. Names come from public_profiles on the server.

import React, { useCallback, useEffect, useState } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ActivityIndicator, Switch, TextInput,
  Alert, Platform,
} from 'react-native';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CURRENT_TOS_VERSION, parseTosVersion, compareTosVersions } from '../constants/tosUpdate';

const COLORS = {
  CARD: '#111',
  GOLD: '#FCD000',
  TEXT: '#fff',
  DIM: '#888',
  BORDER: '#222',
  SUCCESS: '#4CAF50',
  DANGER: '#ff4444',
  WARN: '#f5c542',
};

// Admin strings (5.14).
export const PURCHASE_LIMITS_COPY = {
  title: 'Purchase limits',
  lead: 'Who is limited, and what happens past the line. Changes apply within a minute.',
  modeCap: 'Daily cap (old rule): every account is refused past $500 in 24 hours, including first membership payments and tier upgrades. No card checks, no review hold.',
  modeLimits: "Purchase limits: unverified accounts are refused past $500 in 24 hours; verified accounts have no daily limit, and past the review line the creator's share waits up to the hold days.",
  cardChecksLabel: 'Card checks',
  cardChecksDesc: 'Limit each card to $500 in 24 hours across unverified accounts, apply the review line to each card across all accounts, block cards after a dispute, and hold new-card Checkout payments until the card is checked.',
  warnCardChecksBefore16: 'Terms v1.5 do not describe card checks. Turn them on only after Terms v1.6 is live.',
  warnCardChecksOff16: 'Terms v1.6 describe card checks. Turning them off favors fans but removes the stolen-card guard. Use it only in an emergency, and note why.',
  warnCap16: 'Terms v1.6 say verified accounts have no daily limit. Switching back needs a Terms change.',
  reviewLine: 'Review line',
  holdDays: 'Hold days (2 to 30)',
  note: 'Note (required)',
  save: 'Save',
  sharesTitle: 'Shares in review',
  sharesIncomplete: 'List incomplete: more shares are held than this list can show.',
  releaseNow: 'Release now',
  withhold: 'Withhold',
  release: 'Release',
  cardsTitle: 'Cards',
  cardsEmpty: 'No blocked or busy cards.',
  block: 'Block',
  unblock: 'Unblock',
  whyPrompt: 'Why? (saved in the audit log)',
  chipBlocked: 'Blocked',
  chipUnblocked: 'Unblocked',
  chipAtLine: 'At the line',
  // The card detail (admin only; not fan copy).
  payments: 'Payments',
  hidePayments: 'Hide payments',
  paymentsEmpty: 'No Super Crits, Rumbles or memberships on this card.',
  paymentsIncomplete: 'List incomplete: more payments used this card than this list can show.',
  paymentsPmKey: 'This card has no fingerprint, so its membership charges cannot be listed.',
};

// A tip purchase whose creator shares may still be pending (withhold acts
// on every share of the payment; a share no longer pending is reported).
const WITHHOLDABLE_PURCHASE = new Set(['completed', 'charged']);

const C = PURCHASE_LIMITS_COPY;

// Middle dot kept as an escape so this source stays ASCII.
const DOT = ' \u00b7 ';
const fmtUsd = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
const fmtDate = (ms) => (Number.isFinite(Number(ms)) && ms
  ? new Date(Number(ms)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : 'an unknown date');
const fmtTime = (ms) => new Date(Number(ms)).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});
const WALLET_LABELS = { apple_pay: 'Apple Pay', google_pay: 'Google Pay' };
const SOURCE_LABELS = {
  super_crit: 'Super Crit', team_super_crit: 'Squad Super Crit', rumble: 'Rumble',
  membership: 'Membership', team_membership: 'Team membership',
};
const STATE_LABELS = { held: 'Held', withheld: 'Withheld', released: 'Released early', stamped: 'In review' };

const TOS_AT_LEAST_16 = (() => {
  const have = parseTosVersion(CURRENT_TOS_VERSION);
  return !!have && compareTosVersions(have, [1, 6]) >= 0;
})();

// The mode sentence with its label ("Daily cap (old rule):") in bold; the
// text renders exactly as written.
const splitLabel = (s) => {
  const i = s.indexOf(':');
  return i < 0 ? [s, ''] : [s.slice(0, i + 1), s.slice(i + 1)];
};

function draftFrom(config) {
  return {
    mode: config?.mode === 'limits' ? 'limits' : 'cap',
    cardChecks: config?.mode === 'limits' && config?.cardChecks === true,
    reviewLine: Number.isFinite(Number(config?.reviewLineCents)) ? String(Number(config.reviewLineCents) / 100) : '',
    holdDays: Number.isFinite(Number(config?.holdDays)) ? String(Number(config.holdDays)) : '',
    note: '',
  };
}

function askNote(message) {
  if (Platform.OS === 'web') return Promise.resolve(typeof window !== 'undefined' ? window.prompt(message, '') : null);
  return new Promise((resolve) => {
    Alert.prompt(C.title, message, (v) => resolve(v), 'plain-text', '');
  });
}

function showProblem(msg) {
  if (Platform.OS === 'web') { if (typeof window !== 'undefined') window.alert('Error: ' + msg); return; }
  Alert.alert('Error', msg);
}

export default function PurchaseLimitsAdmin() {
  const functions = getFunctions();
  const [shares, setShares] = useState(null);     // adminListTipReviewShares reply
  const [cards, setCards] = useState(null);       // adminListCardLimits reply
  const [loadError, setLoadError] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);         // row id or card key in flight
  const [openCard, setOpenCard] = useState(null); // card key whose payments are shown
  const [cardDetail, setCardDetail] = useState(null); // adminListCardLimits({cardKey}) reply

  const loadShares = useCallback(async (resetDraft) => {
    try {
      const fn = httpsCallable(functions, 'adminListTipReviewShares');
      const result = await fn({});
      const data = result.data || null;
      setShares(data);
      setLoadError(null);
      if (resetDraft) setDraft(draftFrom(data?.config));
    } catch (e) {
      console.warn('[purchase-limits] shares fetch failed:', e.message);
      setLoadError(e.message || 'Could not load.');
    }
  }, [functions]);

  const loadCards = useCallback(async () => {
    try {
      const fn = httpsCallable(functions, 'adminListCardLimits');
      const result = await fn({});
      setCards(result.data || { cards: [] });
    } catch (e) {
      console.warn('[purchase-limits] cards fetch failed:', e.message);
      setCards({ cards: [], failed: e.message || 'Could not load.' });
    }
  }, [functions]);

  useEffect(() => {
    loadShares(true);
    loadCards();
  }, [loadShares, loadCards]);

  const stored = shares?.config || null;

  const save = async () => {
    if (!draft || saving) return;
    const note = String(draft.note || '').trim();
    if (!note) { showProblem('A note is required.'); return; }
    const payload = { mode: draft.mode, cardChecks: draft.mode === 'limits' && draft.cardChecks === true, note };
    const lineText = String(draft.reviewLine || '').replace(/[$,\s]/g, '');
    if (lineText) {
      const n = Number(lineText);
      // A non-number goes as typed: the server refuses it with its own sentence.
      payload.reviewLineCents = Number.isFinite(n) ? Math.round(n * 100) : lineText;
    }
    const holdText = String(draft.holdDays || '').trim();
    if (holdText) {
      const n = Number(holdText);
      payload.holdDays = Number.isFinite(n) ? n : holdText;
    }
    setSaving(true);
    try {
      const fn = httpsCallable(functions, 'adminSetTipLimitConfig');
      const result = await fn(payload);
      const cfg = result.data || null;
      setShares((s) => ({ ...(s || {}), config: cfg }));
      setDraft(draftFrom(cfg));
    } catch (e) {
      showProblem(e.message);
    } finally {
      setSaving(false);
    }
  };

  const reviewShare = async (row, action) => {
    const note = await askNote(C.whyPrompt);
    if (!note || !String(note).trim()) return;
    setBusy(row.rowId);
    try {
      const fn = httpsCallable(functions, 'adminReviewTipShare');
      const result = await fn({ rowId: row.rowId, action, note: String(note).trim() });
      const failed = (result.data?.results || []).find((r) => r && r.ok === false);
      if (failed) showProblem(failed.error || 'That share was not changed.');
      await loadShares(false);
    } catch (e) {
      showProblem(e.message);
    } finally {
      setBusy(null);
    }
  };

  const loadCardDetail = useCallback(async (cardKey) => {
    try {
      const fn = httpsCallable(functions, 'adminListCardLimits');
      const result = await fn({ cardKey });
      setCardDetail({ ...(result.data || {}), cardKey });
    } catch (e) {
      console.warn('[purchase-limits] card detail fetch failed:', e.message);
      setCardDetail({ cardKey, failed: e.message || 'Could not load.' });
    }
  }, [functions]);

  const toggleCardDetail = async (card) => {
    if (openCard === card.cardKey) { setOpenCard(null); setCardDetail(null); return; }
    setOpenCard(card.cardKey);
    setCardDetail(null);
    await loadCardDetail(card.cardKey);
  };

  // Every pending Super Crit / Rumble share of one payment -> withheld.
  const withholdPayment = async (p) => {
    const note = await askNote(C.whyPrompt);
    if (!note || !String(note).trim()) return;
    setBusy(p.paymentIntentId);
    try {
      const fn = httpsCallable(functions, 'adminReviewTipShare');
      const result = await fn({ paymentIntentId: p.paymentIntentId, action: 'withhold', note: String(note).trim() });
      const failed = (result.data?.results || []).filter((r) => r && r.ok === false);
      if (failed.length) showProblem(failed.map((r) => r.error || 'That share was not changed.').join(' '));
      await Promise.all([loadShares(false), loadCardDetail(p.cardKey)]);
    } catch (e) {
      showProblem(e.message);
    } finally {
      setBusy(null);
    }
  };

  // One membership credit -> withheld, or back to pending.
  const reviewMembershipCredit = async (m, release) => {
    const note = await askNote(C.whyPrompt);
    if (!note || !String(note).trim()) return;
    setBusy(m.creditRowId);
    try {
      const fn = httpsCallable(functions, 'adminWithholdMembershipCredit');
      await fn({ rowId: m.creditRowId, note: String(note).trim(), ...(release ? { release: true } : {}) });
      await Promise.all([loadShares(false), loadCardDetail(m.cardKey)]);
    } catch (e) {
      showProblem(e.message);
    } finally {
      setBusy(null);
    }
  };

  const setCardBlock = async (card, action) => {
    const note = await askNote(C.whyPrompt);
    if (!note || !String(note).trim()) return;
    setBusy(card.cardKey);
    try {
      const fn = httpsCallable(functions, 'adminSetCardBlock');
      await fn({ cardKey: card.cardKey, action, note: String(note).trim() });
      await loadCards();
    } catch (e) {
      showProblem(e.message);
    } finally {
      setBusy(null);
    }
  };

  // ---------------------------------------------------------------- render
  if (!shares && !loadError) {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{C.title}</Text>
        <ActivityIndicator color={COLORS.GOLD} style={styles.spinner} />
      </View>
    );
  }
  if (!shares && loadError) {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{C.title}</Text>
        <Text style={styles.errorText}>{loadError}</Text>
        <TouchableOpacity style={styles.smallBtn} onPress={() => loadShares(true)}>
          <Text style={styles.smallBtnText}>Retry</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const d = draft || draftFrom(stored);
  const setD = (patch) => setDraft((cur) => ({ ...(cur || draftFrom(stored)), ...patch }));
  const cardChecksOn = d.mode === 'limits' && d.cardChecks === true;
  const warnings = [];
  if (!TOS_AT_LEAST_16 && cardChecksOn) warnings.push(C.warnCardChecksBefore16);
  if (TOS_AT_LEAST_16 && stored?.cardChecks === true && !cardChecksOn) warnings.push(C.warnCardChecksOff16);
  if (TOS_AT_LEAST_16 && d.mode === 'cap' && stored?.mode === 'limits') warnings.push(C.warnCap16);

  const modeOption = (value, sentence) => {
    const [label, rest] = splitLabel(sentence);
    const active = d.mode === value;
    return (
      <TouchableOpacity
        key={value}
        style={[styles.modeOption, active && styles.modeOptionActive]}
        onPress={() => setD(value === 'cap' ? { mode: 'cap', cardChecks: false } : { mode: 'limits' })}
        disabled={saving}
        accessibilityRole="radio"
        accessibilityState={{ checked: active, disabled: saving }}
      >
        <View style={[styles.radio, active && styles.radioOn]} />
        <Text style={styles.modeText}>
          <Text style={styles.modeLabel}>{label}</Text>{rest}
        </Text>
      </TouchableOpacity>
    );
  };

  const rows = Array.isArray(shares?.rows) ? shares.rows : [];
  const cardRows = (Array.isArray(cards?.cards) ? cards.cards : [])
    .filter((c) => c && (c.block || c.locked || (Number(c.accounts) || 0) > 0));

  // One card's payments (adminListCardLimits {cardKey}).
  const renderCardDetail = (cardKey, detail) => {
    if (!detail) return <ActivityIndicator color={COLORS.GOLD} style={styles.spinner} />;
    if (detail.failed) return <Text style={styles.errorText}>{detail.failed}</Text>;
    const purchases = Array.isArray(detail.purchases) ? detail.purchases : [];
    const memberships = Array.isArray(detail.memberships) ? detail.memberships : [];
    return (
      <View style={styles.detail}>
        {detail.truncated ? <Text style={styles.warnText}>{C.paymentsIncomplete}</Text> : null}
        {detail.membershipsNote === 'pm_key' ? <Text style={styles.rowMeta}>{C.paymentsPmKey}</Text> : null}
        {purchases.length === 0 && memberships.length === 0 ? <Text style={styles.empty}>{C.paymentsEmpty}</Text> : null}
        {purchases.map((p) => (
          <View key={`${p.kind}_${p.purchaseId}`} style={styles.detailRow}>
            <View style={styles.rowInfo}>
              <Text style={styles.rowMeta}>
                {`${SOURCE_LABELS[p.kind] || p.kind || 'Purchase'}${DOT}${fmtUsd(p.amountCents)}${DOT}from ${p.name || p.uid || 'Unknown'}`}
              </Text>
              <Text style={styles.rowKey}>{[p.status || '-', fmtDate(p.createdAtMs), p.paymentIntentId || ''].filter(Boolean).join(DOT)}</Text>
            </View>
            {p.paymentIntentId && WITHHOLDABLE_PURCHASE.has(p.status) ? (
              <TouchableOpacity style={styles.smallBtnDanger} onPress={() => withholdPayment({ ...p, cardKey })} disabled={busy === p.paymentIntentId}>
                <Text style={styles.smallBtnDangerText}>{C.withhold}</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ))}
        {memberships.map((m) => (
          <View key={m.revenueRowId} style={styles.detailRow}>
            <View style={styles.rowInfo}>
              <Text style={styles.rowMeta}>
                {`${SOURCE_LABELS.membership}${DOT}${fmtUsd(m.amountCents)}${DOT}from ${m.payerName || m.payerUid || 'Unknown'} to ${m.creatorName || m.creatorUid || 'Unknown'}`}
              </Text>
              <Text style={styles.rowKey}>{[m.creditStatus || '-', fmtDate(m.chargedAtMs), m.creditRowId || ''].filter(Boolean).join(DOT)}</Text>
            </View>
            {m.creditRowId && m.creditStatus === 'pending' ? (
              <TouchableOpacity style={styles.smallBtnDanger} onPress={() => reviewMembershipCredit({ ...m, cardKey }, false)} disabled={busy === m.creditRowId}>
                <Text style={styles.smallBtnDangerText}>{C.withhold}</Text>
              </TouchableOpacity>
            ) : null}
            {m.creditRowId && m.creditStatus === 'withheld_review' ? (
              <TouchableOpacity style={styles.smallBtn} onPress={() => reviewMembershipCredit({ ...m, cardKey }, true)} disabled={busy === m.creditRowId}>
                <Text style={styles.smallBtnText}>{C.release}</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        ))}
      </View>
    );
  };

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{C.title}</Text>
      <Text style={styles.lead}>{C.lead}</Text>

      {/* The two switches, the review line and the hold days */}
      {modeOption('cap', C.modeCap)}
      {modeOption('limits', C.modeLimits)}

      <View style={styles.toggleRow}>
        <View style={styles.toggleInfo}>
          <Text style={styles.toggleLabel}>{C.cardChecksLabel}</Text>
          <Text style={styles.toggleDesc}>{C.cardChecksDesc}</Text>
        </View>
        <Switch
          value={cardChecksOn}
          onValueChange={(v) => setD({ cardChecks: v })}
          disabled={saving || d.mode !== 'limits'}
          trackColor={{ false: '#333', true: '#4CAF50' }}
          thumbColor={cardChecksOn ? '#fff' : '#888'}
        />
      </View>

      {warnings.map((w) => <Text key={w} style={styles.warnText}>{w}</Text>)}

      <View style={styles.fieldsRow}>
        <View style={styles.field}>
          <Text style={styles.fieldLabel}>{C.reviewLine}</Text>
          <TextInput
            style={styles.input}
            value={d.reviewLine}
            onChangeText={(v) => setD({ reviewLine: v })}
            keyboardType="decimal-pad"
            placeholder="500"
            placeholderTextColor="#666"
            editable={!saving}
          />
        </View>
        <View style={styles.field}>
          <Text style={styles.fieldLabel}>{C.holdDays}</Text>
          <TextInput
            style={styles.input}
            value={d.holdDays}
            onChangeText={(v) => setD({ holdDays: v })}
            keyboardType="number-pad"
            placeholder="30"
            placeholderTextColor="#666"
            editable={!saving}
          />
        </View>
      </View>
      <Text style={styles.fieldLabel}>{C.note}</Text>
      <TextInput
        style={[styles.input, styles.noteInput]}
        value={d.note}
        onChangeText={(v) => setD({ note: v })}
        placeholderTextColor="#666"
        editable={!saving}
        multiline
      />
      <TouchableOpacity
        style={[styles.saveBtn, (saving || !String(d.note || '').trim()) && styles.btnDisabled]}
        onPress={save}
        disabled={saving || !String(d.note || '').trim()}
      >
        {saving ? <ActivityIndicator color="#000" /> : <Text style={styles.saveBtnText}>{C.save}</Text>}
      </TouchableOpacity>

      {/* Shares in review */}
      <Text style={[styles.subTitle, styles.subTitleGap]}>{C.sharesTitle}</Text>
      {shares?.truncated ? <Text style={styles.warnText}>{C.sharesIncomplete}</Text> : null}
      {rows.length === 0 ? <Text style={styles.empty}>None.</Text> : rows.map((r) => {
        const pending = r.status === 'pending';
        const dateLine = r.state === 'withheld'
          ? `Withheld ${fmtDate(r.withheld?.atMs || r.createdAtMs)}`
          : `Until ${fmtDate(r.availableAtMs)}`;
        const flags = [r.disputed ? 'Disputed' : null, r.refunded ? 'Refunded' : null].filter(Boolean);
        return (
          <View key={r.rowId} style={styles.row}>
            <View style={styles.rowInfo}>
              <Text style={styles.rowName}>
                {r.recipientName || r.recipientUid || 'Unknown'}
                <Text style={styles.rowChip}>{`  ${STATE_LABELS[r.state] || r.state || ''}`}</Text>
              </Text>
              <Text style={styles.rowMeta}>
                {`${SOURCE_LABELS[r.sourceType] || r.sourceType || 'Share'}${DOT}${fmtUsd(r.netCents)} of ${fmtUsd(r.grossCents)}`}
                {r.payerName || r.payerUid ? `${DOT}from ${r.payerName || r.payerUid}` : ''}
              </Text>
              <Text style={styles.rowMeta}>{[dateLine, ...flags].join(DOT)}</Text>
            </View>
            {r.tip ? (
              <View style={styles.rowBtns}>
                {pending && r.state === 'held' ? (
                  <TouchableOpacity style={styles.smallBtn} onPress={() => reviewShare(r, 'release_now')} disabled={busy === r.rowId}>
                    <Text style={styles.smallBtnText}>{C.releaseNow}</Text>
                  </TouchableOpacity>
                ) : null}
                {pending ? (
                  <TouchableOpacity style={styles.smallBtnDanger} onPress={() => reviewShare(r, 'withhold')} disabled={busy === r.rowId}>
                    <Text style={styles.smallBtnDangerText}>{C.withhold}</Text>
                  </TouchableOpacity>
                ) : null}
                {r.state === 'withheld' ? (
                  <TouchableOpacity style={styles.smallBtn} onPress={() => reviewShare(r, 'release')} disabled={busy === r.rowId}>
                    <Text style={styles.smallBtnText}>{C.release}</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            ) : null}
          </View>
        );
      })}

      {/* Cards */}
      <Text style={[styles.subTitle, styles.subTitleGap]}>{C.cardsTitle}</Text>
      {cards === null ? <ActivityIndicator color={COLORS.GOLD} style={styles.spinner} /> : null}
      {cards?.failed ? <Text style={styles.errorText}>{cards.failed}</Text> : null}
      {cards && !cards.failed && cardRows.length === 0 ? <Text style={styles.empty}>{C.cardsEmpty}</Text> : null}
      {cardRows.map((c) => {
        const blocked = c.block?.status === 'blocked';
        const chip = blocked ? C.chipBlocked : (c.locked ? C.chipAtLine : (c.block?.status === 'unblocked' ? C.chipUnblocked : null));
        const wallet = WALLET_LABELS[c.wallet] || null;
        const title = `${c.brand || 'Card'} ending ${c.last4 || '----'}${wallet ? `${DOT}${wallet}` : ''}`;
        let blockLine = null;
        if (blocked) {
          if (c.block.reason === 'efw') blockLine = `Blocked after a bank fraud report on ${fmtDate(c.block.atMs)}`;
          else if (c.block.reason === 'admin') blockLine = `Blocked by an admin on ${fmtDate(c.block.atMs)}`;
          else blockLine = `Blocked after a dispute on ${fmtDate(c.block.atMs)}`;
        }
        const n = Number(c.unverifiedAccounts) || 0;
        const windowLine = n > 0 || (Number(c.windowCents) || 0) > 0
          ? `${n} unverified accounts in the last 24 hours, ${fmtUsd(c.windowCents)}.${Number.isFinite(Number(c.opensAtMs)) && c.opensAtMs ? ` Opens for a $1 purchase at ${fmtTime(c.opensAtMs)}.` : ''}`
          : null;
        const open = openCard === c.cardKey;
        const detail = open && cardDetail && cardDetail.cardKey === c.cardKey ? cardDetail : null;
        return (
          <View key={c.cardKey}>
            <View style={styles.row}>
              <View style={styles.rowInfo}>
                <Text style={styles.rowName}>
                  {title}
                  {chip ? <Text style={[styles.rowChip, blocked && styles.rowChipDanger]}>{`  ${chip}`}</Text> : null}
                </Text>
                {blockLine ? <Text style={styles.rowMeta}>{blockLine}</Text> : null}
                {windowLine ? <Text style={styles.rowMeta}>{windowLine}</Text> : null}
                <Text style={styles.rowKey}>{c.cardKey}</Text>
              </View>
              <View style={styles.rowBtns}>
                {blocked ? (
                  <TouchableOpacity style={styles.smallBtn} onPress={() => setCardBlock(c, 'unblock')} disabled={busy === c.cardKey}>
                    <Text style={styles.smallBtnText}>{C.unblock}</Text>
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity style={styles.smallBtnDanger} onPress={() => setCardBlock(c, 'block')} disabled={busy === c.cardKey}>
                    <Text style={styles.smallBtnDangerText}>{C.block}</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity style={styles.smallBtnPlain} onPress={() => toggleCardDetail(c)}>
                  <Text style={styles.smallBtnPlainText}>{open ? C.hidePayments : C.payments}</Text>
                </TouchableOpacity>
              </View>
            </View>
            {open ? renderCardDetail(c.cardKey, detail) : null}
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    backgroundColor: COLORS.CARD, borderRadius: 12, padding: 16, marginBottom: 16,
    borderWidth: 1, borderColor: COLORS.BORDER,
  },
  sectionTitle: {
    color: COLORS.GOLD, fontSize: 14, fontWeight: '700', marginBottom: 12,
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  lead: { color: '#aaa', fontSize: 13, marginBottom: 12 },
  spinner: { marginTop: 12 },
  errorText: { color: COLORS.DANGER, fontSize: 13, marginTop: 8 },
  warnText: { color: COLORS.WARN, fontSize: 12.5, marginTop: 8, lineHeight: 17 },
  modeOption: {
    flexDirection: 'row', alignItems: 'flex-start', padding: 10, borderRadius: 8,
    borderWidth: 1, borderColor: '#2a2a2a', marginBottom: 8,
  },
  modeOptionActive: { borderColor: COLORS.GOLD, backgroundColor: 'rgba(252, 208, 0, 0.06)' },
  radio: {
    width: 14, height: 14, borderRadius: 7, borderWidth: 2, borderColor: '#555',
    marginRight: 10, marginTop: 2,
  },
  radioOn: { borderColor: COLORS.GOLD, backgroundColor: COLORS.GOLD },
  modeText: { flex: 1, color: '#ccc', fontSize: 13, lineHeight: 18 },
  modeLabel: { color: COLORS.TEXT, fontWeight: '700' },
  toggleRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: COLORS.BORDER,
  },
  toggleInfo: { flex: 1, marginRight: 16 },
  toggleLabel: { color: COLORS.TEXT, fontSize: 16, fontWeight: '600' },
  toggleDesc: { color: COLORS.DIM, fontSize: 13, marginTop: 2 },
  fieldsRow: { flexDirection: 'row', gap: 12, marginTop: 12 },
  field: { flex: 1 },
  fieldLabel: { color: COLORS.DIM, fontSize: 12, marginBottom: 6, marginTop: 8 },
  input: {
    backgroundColor: '#1a1a1a', borderRadius: 8, padding: 12, color: COLORS.TEXT,
    fontSize: 16, borderWidth: 1, borderColor: '#333',
  },
  noteInput: { minHeight: 56, fontSize: 14 },
  saveBtn: {
    backgroundColor: COLORS.GOLD, borderRadius: 8, paddingVertical: 12, alignItems: 'center', marginTop: 12,
  },
  saveBtnText: { color: '#000', fontSize: 14, fontWeight: '800' },
  btnDisabled: { opacity: 0.5 },
  subTitle: { color: COLORS.TEXT, fontSize: 14, fontWeight: '700' },
  subTitleGap: { marginTop: 20 },
  empty: { color: '#666', fontSize: 13, marginTop: 8, fontStyle: 'italic' },
  row: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 10,
    padding: 12, borderRadius: 8, borderWidth: 1, borderColor: '#2a2a2a', backgroundColor: '#151515',
  },
  rowInfo: { flex: 1, marginRight: 12 },
  rowName: { color: COLORS.TEXT, fontSize: 14, fontWeight: '700' },
  rowChip: { color: COLORS.GOLD, fontSize: 12, fontWeight: '700' },
  rowChipDanger: { color: COLORS.DANGER },
  rowMeta: { color: '#aaa', fontSize: 12, marginTop: 2 },
  rowKey: {
    color: '#555', fontSize: 10.5, marginTop: 4,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  rowBtns: { gap: 6 },
  smallBtn: { paddingVertical: 7, paddingHorizontal: 12, borderRadius: 8, backgroundColor: COLORS.GOLD, alignItems: 'center' },
  smallBtnText: { color: '#000', fontSize: 12.5, fontWeight: '700' },
  smallBtnDanger: {
    paddingVertical: 7, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1,
    borderColor: COLORS.DANGER, alignItems: 'center',
  },
  smallBtnDangerText: { color: COLORS.DANGER, fontSize: 12.5, fontWeight: '700' },
  smallBtnPlain: {
    paddingVertical: 7, paddingHorizontal: 12, borderRadius: 8, borderWidth: 1,
    borderColor: '#444', alignItems: 'center',
  },
  smallBtnPlainText: { color: COLORS.TEXT, fontSize: 12.5, fontWeight: '700' },
  detail: { marginLeft: 12, paddingLeft: 10, borderLeftWidth: 2, borderLeftColor: '#2a2a2a', marginTop: 4 },
  detailRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 6,
    paddingVertical: 8, paddingHorizontal: 10, borderRadius: 8, backgroundColor: '#121212',
  },
});
